import React from 'react';
import { usePrayerChoice } from '@/contexts/PrayerChoiceContext';
import { usePrayerCompanionAccess } from '@/hooks/usePrayerCompanionAccess';
import { usePrayerCompanionUsage } from '@/hooks/usePrayerCompanionUsage';
import { useToast } from '@/hooks/use-toast';
import { CompanionHeader } from './CompanionHeader';
import { UnifiedChatInterface } from './UnifiedChatInterface';
import { BibleStudyInterface } from './BibleStudyInterface';
import AbielPrayerChat from './AbielPrayerChat';
import RealtimeChatInterface from './RealtimeChatInterface';
import { ChatMode } from '@/types/chat';
import type { PrayerCompanionMode } from '@/types/prayerCompanion.types';

export function CompanionContent() {
  const { closeCompanion, setCompanionMode, companionMode } = usePrayerChoice();
  const { canInteractWithMode, getAccessDeniedReason } = usePrayerCompanionAccess();
  const { incrementUsage } = usePrayerCompanionUsage();
  const { toast } = useToast();

  // Map companion mode to ChatMode for the BibleChatInterface components
  const currentChatMode: ChatMode = companionMode === 'chat' ? 'study' : 'regular';

  const handleOpenAiAssistant = async () => {
    if (!canInteractWithMode('prayer')) {
      const reason = getAccessDeniedReason('prayer');
      if (reason === 'NO_ACCESS') {
        toast({
          title: "Premium feature",
          description: "Upgrade to premium to unlock guided prayer mode and enjoy unlimited access.",
          duration: 4000,
        });
      } else if (reason === 'DAILY_LIMIT') {
        toast({
          title: "Daily limit reached",
          description: "You've reached your daily limit for prayer mode. Upgrade to premium for unlimited access or try again tomorrow.",
          duration: 4000,
        });
      }
      return;
    }

    // Track usage before opening
    const success = await incrementUsage('prayer');
    if (success) {
      setCompanionMode('prayer');
    }
  };

  const handleModeChange = async (mode: ChatMode) => {
    // Map ChatMode to companion mode and check access
    const companionModeMap: Record<ChatMode, PrayerCompanionMode> = {
      'study': 'chat',
      'regular': 'cards'
    };

    const targetMode = companionModeMap[mode];
    
    if (!canInteractWithMode(targetMode)) {
      const reason = getAccessDeniedReason(targetMode);
      if (reason === 'NO_ACCESS') {
        toast({
          title: targetMode === 'chat' ? "Premium feature" : "Sign in required",
          description: targetMode === 'chat' 
            ? "Upgrade to premium to unlock chat mode and enjoy unlimited access."
            : "Please sign in to access this feature. It's free to get started!",
          duration: 4000,
        });
      } else if (reason === 'DAILY_LIMIT') {
        toast({
          title: "Daily limit reached",
          description: `You've reached your daily limit for ${targetMode} mode. Upgrade to premium for unlimited access or try again tomorrow.`,
          duration: 4000,
        });
      }
      return;
    }

    // Track usage before changing mode
    const success = await incrementUsage(targetMode);
    if (success) {
      setCompanionMode(targetMode);
    }
  };

  return (
    <div className="flex flex-col h-full w-full companion-content">
      {/* NOTE: companion-content is used by CSS to add padding equal to the footer height */}
      {/* Fixed-height header inside the experience */}
      <div className="flex-shrink-0">
        <CompanionHeader
          onClose={closeCompanion}
          onOpenAiAssistant={handleOpenAiAssistant}
          onModeChange={handleModeChange}
          currentMode={currentChatMode}
        />
      </div>

      {/*
        Body takes remaining space. We add pb via CSS (.companion-content)
        so the chat composer never sits under the BottomNavigation.
      */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {(() => {
          if (companionMode === 'prayer') {
            return <AbielPrayerChat />;
          } else if (companionMode === 'chat') {
            return <BibleStudyInterface />;
          } else if (companionMode === 'realtime') {
            return <RealtimeChatInterface />;
          } else {
            return <UnifiedChatInterface mode={currentChatMode} />;
          }
        })()}
      </div>
    </div>
  );
}
